import React from 'react';
import styled, { useTheme } from 'styled-components';

import FjGlobalHeader from 'components/global-header';
import { FjSearchInput } from 'components/primitives';
import HelpNoCircle from 'icons/help-no-circle';
import { AuthClientUtils } from 'ducks/auth-client';
import { routeIsAdmin, isSuperAdmin, getUserInitials, chinaFlag } from 'utils';
import { HeaderActions } from 'actions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  selectCurrentUser,
  selectIsAuthenticated,
  selectFilterKeyword,
  selectIsAdminUser,
  displayKeywordFilter,
} from 'selectors';

import AccountDropdown from './account-dropdown';
import CostCenterSelector from '../cost-center-selector';

const GlobalHeader = styled(FjGlobalHeader)`
  background-color: ${({ theme }) => theme.colors.black};
`;

const SelectContainer = styled.div`
  flex: 1;
  align-self: stretch;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 6px;
  margin-right: 20px;
  display: flex;
  flex-direction: row;
`;

const IconPad = styled.div`
  padding-top: 5px;
  color: white;
`;

const SearchInput = styled(FjSearchInput).attrs({
  bgColor: 'white',
  collapsedIconColor: 'white',
  // height: '50px',
  width: '300px',
  fontSize: '14px',
  iconSize: '20px',
  iconBgColor: '#231f20',
  autoCollapse: { size: '35px' },
})`
  margin-right: 10px;
`;

interface CurrentUser {
  firstname?: string;
  lastname?: string;
}

interface RenderMenuArgs {
  onClose: () => void;
}

const Header = (): React.JSX.Element => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  // baseSelect selectors are typed `unknown`; filterKeyword is the header search string.
  const filterKeyword = useAppSelector(selectFilterKeyword) as string;
  const isAdminUser = useAppSelector(selectIsAdminUser);
  const currentUser = useAppSelector(selectCurrentUser) as CurrentUser;
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const showSearchInput = useAppSelector(displayKeywordFilter);

  const { firstname, lastname } = currentUser;
  const setFilterKeyword = (keyword: string): void => {
    dispatch(HeaderActions.setFilterKeyword(keyword));
  };

  const getHeaderText = (): string => {
    if (routeIsAdmin()) {
      return `(Admin)`;
    }
    // Also requires the sudo claim, not just the build flag + auth state — otherwise a
    // non-sudo user sees "(SuperAdmin)" in the header directly above the "Not Authorized"
    // message router.tsx renders for them, which is exactly the confusion FJCLOUD-4468 asks
    // this header text to prevent.
    if (isSuperAdmin() && isAuthenticated && AuthClientUtils.currentUserHasSudoClaim()) {
      return `(SuperAdmin)`;
    }
    return '';
  };
  const setFilter = ({ target }: React.ChangeEvent<HTMLInputElement>): void =>
    setFilterKeyword((target && target.value) || '');
  const launchHelp = (): Window | null => window.open('http://docs.flowjo.com/cloud');

  // Assigned to a variable so the object is structurally checked (extra
  // color/border keys allowed) instead of triggering excess-property checks.
  const fjAvatarStyle = {
    backgroundColor: theme.colors.selectableLightBg,
    color: theme.colors.white,
    fontSize: theme.fontSizes.h4,
    border: 0,
  };

  return (
    <GlobalHeader
      authenticated={isAuthenticated}
      initials={getUserInitials({
        firstname,
        lastname,
      })}
      headerText={getHeaderText()}
      showAvatar
      fjAvatarStyle={fjAvatarStyle}
      renderMenu={({ onClose }: RenderMenuArgs) => (
        <AccountDropdown isAdmin={isAdminUser} closeMenu={onClose} />
      )}
    >
      {isAuthenticated && (
        <SelectContainer>
          <CostCenterSelector />
        </SelectContainer>
      )}

      {showSearchInput && (
        <SearchInput
          onChange={setFilter}
          value={filterKeyword}
          // placeholder="filter results"
          // autoCollapse={{ size: '130px' }}
          // height="30px"
          // width="320px"
        />
      )}

      {isAuthenticated && !chinaFlag() && (
        <button
          type="button"
          style={{
            marginRight: '20px',
            backgroundColor: theme.colors.black,
            borderColor: theme.colors.black,
            cursor: 'pointer',
            border: 'none',
          }}
          onClick={() => {
            launchHelp();
          }}
        >
          <IconPad>
            <HelpNoCircle size="20px" />
          </IconPad>
        </button>
      )}
    </GlobalHeader>
  );
};

export default Header;
